// src/services/scratchService.js
//
// Service layer for the Scratch-Off Intelligence module.
//
// Mirrors the conventions in resultsService.js: JSDoc typedefs that match the
// backend DTOs, all calls go through the shared axiosInstance (so the JWT
// cookie + X-CSRF-Token + cache-buster behavior is inherited), and list
// endpoints always return an array even on error so callers can render safely.
//
// Endpoints live under /site/scratch/* (user) — the same prefix scheme the rest
// of the app uses — so nothing about auth/baseURL needs to change.
//
// File location note: recommended path is `src/services/`.

import axiosInstance from "../utils/AxiosInstance";

/* ============================================================
   Types (JSDoc) — matches backend DTOs
   ============================================================ */

/**
 * @typedef {Object} ScratchStateDto
 * @property {string}  stateCode         Two-letter code, e.g. "TX"
 * @property {string}  stateName
 * @property {number}  activeGameCount
 * @property {string|null} lastUpdated   ISO date of newest snapshot
 *
 * @typedef {Object} ScratchGameSummaryDto
 * @property {number}  id
 * @property {string}  stateCode
 * @property {string}  gameNumber
 * @property {string}  gameName
 * @property {number}  ticketPrice
 * @property {string}  status                     "ACTIVE" | "CLOSING" | "ENDED" | "UNKNOWN"
 * @property {number}  scratchScore               0..100
 * @property {number}  topPrizeRemainingRatio     0..1
 * @property {number}  totalPrizeRemainingRatio   0..1
 * @property {number}  estimatedEv                relative EV (state-scoped)
 * @property {number}  topPrizesRemaining
 * @property {boolean} topPrizesExhausted
 * @property {number}  topPrizeAmount
 * @property {("UP"|"DOWN"|"FLAT"|null)} scoreTrend
 *
 * @typedef {Object} ScratchPrizeTierDto
 * @property {number}      prizeAmount
 * @property {number|null} prizesPrinted
 * @property {number|null} prizesClaimed
 * @property {number}      prizesRemaining
 * @property {boolean}     topPrize
 * @property {number|null} claimedPct             0..100
 *
 * @typedef {Object} ScratchGameDetailDto
 * @property {number}  id
 * @property {string}  stateCode
 * @property {string}  gameNumber
 * @property {string}  gameName
 * @property {number}  ticketPrice
 * @property {string|null} startDate
 * @property {string|null} endDate
 * @property {string}  status
 * @property {string}  sourceUrl
 * @property {number}  scratchScore
 * @property {number}  topPrizeRemainingRatio
 * @property {number}  totalPrizeRemainingRatio
 * @property {number}  estimatedEv
 * @property {number}  midTierStrength
 * @property {number}  jackpotDensity
 * @property {string|null} lastUpdated
 * @property {ScratchPrizeTierDto[]} prizeTiers
 *
 * @typedef {Object} ScratchScorePointDto
 * @property {string} snapshotDate                YYYY-MM-DD
 * @property {number} scratchScore                0..100
 * @property {number} topPrizeRemainingRatio
 * @property {number} totalPrizeRemainingRatio
 * @property {number} estimatedEv
 *
 * @typedef {Object} ScratchAlertDto
 * @property {number}  id
 * @property {number}  scratchGameId
 * @property {string}  alertType
 * @property {string}  title
 * @property {string}  message
 * @property {boolean} read
 * @property {string}  createdAt                  ISO timestamp
 *
 * @typedef {Object} ScratchPreferencesDto
 * @property {number}      id
 * @property {string|null} stateCode              null = "all states"
 * @property {number|null} minTicketPrice
 * @property {number|null} maxTicketPrice
 * @property {boolean}     alertOnNewGames
 * @property {boolean}     alertOnScoreChange
 */

/* ============================================================
   States + games
   ============================================================ */

/** @returns {Promise<ScratchStateDto[]>} */
export async function getStates() {
  try {
    const res = await axiosInstance.get("/site/scratch/states");
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

/**
 * Ranked games for a state (best Scratch Score first).
 * @param {string} state
 * @param {{ minPrice?: number, maxPrice?: number, jackpotOnly?: boolean }} [opts]
 * @returns {Promise<ScratchGameSummaryDto[]>}
 */
export async function getRankedGames(state, opts = {}) {
  if (!state) return [];
  try {
    const res = await axiosInstance.get("/site/scratch/games/ranked", {
      params: {
        state,
        minPrice: opts.minPrice,
        maxPrice: opts.maxPrice,
        jackpotOnly: opts.jackpotOnly ? true : undefined,
      },
    });
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

/**
 * Same cohort re-sorted by estimated relative value.
 * @param {string} state
 * @param {{ minPrice?: number, maxPrice?: number }} [opts]
 * @returns {Promise<ScratchGameSummaryDto[]>}
 */
export async function getBestValueGames(state, opts = {}) {
  if (!state) return [];
  try {
    const res = await axiosInstance.get("/site/scratch/games/best-value", {
      params: { state, minPrice: opts.minPrice, maxPrice: opts.maxPrice },
    });
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

/**
 * @param {number} id
 * @returns {Promise<ScratchGameDetailDto|null>}
 */
export async function getGameDetail(id) {
  try {
    const res = await axiosInstance.get(`/site/scratch/games/${id}`);
    return res?.data ?? null;
  } catch {
    return null;
  }
}

/**
 * @param {number} id
 * @returns {Promise<ScratchScorePointDto[]>}
 */
export async function getScoreHistory(id) {
  try {
    const res = await axiosInstance.get(`/site/scratch/games/${id}/history`);
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

/* ============================================================
   Alerts
   ============================================================ */

/** @returns {Promise<ScratchAlertDto[]>} */
export async function getAlerts() {
  try {
    const res = await axiosInstance.get("/site/scratch/alerts");
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

/** @returns {Promise<number>} */
export async function getUnreadAlertCount() {
  try {
    const res = await axiosInstance.get("/site/scratch/alerts/unread-count");
    return Number(res?.data?.count) || 0;
  } catch {
    return 0;
  }
}

/** @returns {Promise<number>} number of alerts marked read */
export async function markAllAlertsRead() {
  try {
    const res = await axiosInstance.post("/site/scratch/alerts/read-all");
    return Number(res?.data?.updated) || 0;
  } catch {
    return 0;
  }
}

/** @param {number} id */
export async function markAlertRead(id) {
  try {
    await axiosInstance.post(`/site/scratch/alerts/${id}/read`);
    return true;
  } catch {
    return false;
  }
}

/* ============================================================
   Preferences
   ============================================================ */

/** @returns {Promise<ScratchPreferencesDto[]>} */
export async function getPreferences() {
  try {
    const res = await axiosInstance.get("/site/scratch/preferences");
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

/**
 * @param {Partial<ScratchPreferencesDto>} prefs
 * @returns {Promise<ScratchPreferencesDto|null>}
 */
export async function savePreferences(prefs) {
  const res = await axiosInstance.post("/site/scratch/preferences", prefs);
  return res?.data ?? null;
}

/* ============================================================
   Formatting helpers (shared by the scratch UI)
   ============================================================ */

/** "$30", "$1,000,000". */
export function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

/** Big counts → "1.2M", "45.3K". */
export function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** 0..1 ratio → "73%". */
export function formatRatioPct(ratio) {
  const n = Number(ratio);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n * 100)}%`;
}

/** Score → integer 0..100. */
export function formatScore(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "—";
  return String(Math.round(n));
}

/**
 * Map a 0..100 score to a qualitative, NON-PREDICTIVE label.
 * Language is intentionally about *relative value of remaining prizes*,
 * never about likelihood of winning.
 * @returns {{ label: string, tone: "gold"|"teal"|"blue"|"neutral"|"red" }}
 */
export function scoreBand(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return { label: "No data", tone: "neutral" };
  if (n >= 75) return { label: "Better relative value", tone: "gold" };
  if (n >= 55) return { label: "Above-average prize strength", tone: "teal" };
  if (n >= 35) return { label: "Average prize strength", tone: "blue" };
  return { label: "Lower remaining prize strength", tone: "neutral" };
}

/** ISO date/timestamp → "May 18, 2026". */
export function formatDate(raw) {
  if (!raw) return "—";
  try {
    const d = new Date(raw.length === 10 ? `${raw}T00:00:00` : raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(raw);
  }
}

/* ============================================================
   Access & monetization
   ============================================================ */

/**
 * @typedef {Object} ScratchAccessDto
 * @property {boolean} hasAccess
 * @property {"NONE"|"PLUS"|"PRO"|"ELITE"} tier
 * @property {number}  allowedStateCount     0 = none, 1 = PLUS, large = PRO/ELITE
 * @property {boolean} advancedAnalytics
 * @property {boolean} alertsAndTrends
 * @property {boolean} riskValueReports
 * @property {number}  currentCredits
 * @property {string[]} allowedStateCodes    optional hint, empty when unlimited
 */

/**
 * Fetch the current user's scratch-off access matrix.
 *
 * Used by every scratch page to decide what to render — falls through to a
 * "no access" record on error (so a network blip doesn't accidentally expose
 * features). Returns a 402 from the backend for non-subscribers, which the
 * Axios interceptor surfaces as an error; we catch and return NONE here so
 * the UI can show the paid-access card without try/catch noise at the call
 * site.
 *
 * @returns {Promise<ScratchAccessDto>}
 */
export async function getScratchAccess() {
  try {
    const res = await axiosInstance.get("/site/scratch/access");
    return res.data || noAccessFallback();
  } catch (e) {
    // 402 = paywall; any other error we also treat as "no access" so the
    // UI degrades safely. The error reason is on e.response.data if a
    // caller wants to render a more specific message.
    return noAccessFallback();
  }
}

function noAccessFallback() {
  return {
    hasAccess: false,
    tier: "NONE",
    allowedStateCount: 0,
    advancedAnalytics: false,
    alertsAndTrends: false,
    riskValueReports: false,
    currentCredits: 0,
    allowedStateCodes: [],
  };
}

/* ============================================================
   Premium: Scratch-Off Risk & Value Report
   ============================================================ */

/**
 * @typedef {Object} ScratchRiskValueReportEntry
 * @property {number}  gameId
 * @property {string}  gameNumber
 * @property {string}  gameName
 * @property {number}  ticketPrice
 * @property {number}  scratchScore
 * @property {number}  topPrizeRemainingRatio
 * @property {number|null} midTierStrength
 * @property {string}  relativeValueLabel   "higher relative value" | "lower relative value"
 * @property {string}  prizeProfileLabel    "stronger remaining prize profile" | "weaker remaining prize profile"
 *
 * @typedef {Object} ScratchRiskValueReportDto
 * @property {string}  state
 * @property {string}  generatedAt
 * @property {string}  dataFreshness
 * @property {number}  totalGamesAnalyzed
 * @property {ScratchRiskValueReportEntry[]} higherRelativeValue
 * @property {ScratchRiskValueReportEntry[]} lowerRelativeValue
 * @property {string[]} disclaimers
 * @property {string}  notice
 */

/**
 * Run the premium Scratch-Off Risk & Value Report.
 *
 * Costs 1 credit per successful run. The backend only debits the credit
 * after the report is generated successfully — failures don't consume
 * credits. Throws an Axios error on 402 (no tier or no credits) so the
 * caller can render the right paywall vs. credit-empty message.
 *
 * @param {{ state: string, minTicketPrice?: number, maxTicketPrice?: number }} opts
 * @returns {Promise<ScratchRiskValueReportDto>}
 */
export async function runRiskValueReport({ state, minTicketPrice, maxTicketPrice }) {
  const body = { state };
  if (minTicketPrice !== undefined && minTicketPrice !== null) {
    body.minTicketPrice = minTicketPrice;
  }
  if (maxTicketPrice !== undefined && maxTicketPrice !== null) {
    body.maxTicketPrice = maxTicketPrice;
  }
  const res = await axiosInstance.post("/site/scratch/reports/risk-value", body);
  return res.data;
}
